import { useState, useEffect, type ReactNode } from "react";
import {
  callFetchHarvestProducts,
  callCreateHarvestProduct,
  callUpdateHarvestProduct,
  callDeleteHarvestProduct,
} from "../../../api/harvestProducts";
import { HarvestContext, type HarvestContextType } from "./HarvestContext";
import type { HarvestProduct } from "../utils/types";
import { useAuthUser } from "../hooks/useAuthUser";

interface HarvestProviderProps {
  children: ReactNode;
}

export const HarvestContextProvider = ({ children }: HarvestProviderProps) => {
  const { isAdmin, isEmployee, isBusiness } = useAuthUser();
  const [harvestProducts, setHarvestProducts] = useState<HarvestProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canAccessHarvestInventory = isAdmin || isEmployee || isBusiness;

  const fetchHarvestProducts = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const data = await callFetchHarvestProducts();
      setHarvestProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching harvest items:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch harvest items"
      );
      setHarvestProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const createHarvestProduct = async (
    name: string,
    quantity: number = 0,
    price: number = 0
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const newItem = await callCreateHarvestProduct({ name, quantity, price });
      setHarvestProducts((prev) => [...prev, newItem]);
    } catch (err) {
      console.error("Error adding harvest item:", err);
      setError(
        err instanceof Error ? err.message : "Failed to add harvest item"
      );
    } finally {
      setLoading(false);
    }
  };

  const updateHarvestProduct = async (
    id: string,
    quantity: number,
    price: number,
    discount: number | null
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const updated = await callUpdateHarvestProduct(id, {
        quantity,
        price,
        discount,
      });
      setHarvestProducts((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                quantity: updated.quantity,
                price: updated.price,
                discount: updated.discount,
                updatedAt: updated.updatedAt,
              }
            : item
        )
      );
    } catch (err) {
      console.error("Error updating harvest item:", err);
      setError(
        err instanceof Error ? err.message : "Failed to update harvest item"
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteHarvestProduct = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await callDeleteHarvestProduct(id);
      setHarvestProducts((prev) => prev.filter((product) => product.id !== id));
    } catch (err) {
      console.error("Error deleting harvest item:", err);
      setError(
        err instanceof Error ? err.message : "Failed to delete harvest item"
      );
    } finally {
      setLoading(false);
    }
  };

  const refreshHarvestProducts = async (): Promise<void> => {
    await fetchHarvestProducts();
  };

  useEffect(() => {
    if (!canAccessHarvestInventory) {
      setHarvestProducts([]);
      setError(null);
      setLoading(false);
      return;
    }
    fetchHarvestProducts();
  }, [canAccessHarvestInventory]);

  async function updateHarvestInventory(updatedInventory: HarvestProduct[]) {
    for (const product of updatedInventory) {
      await updateHarvestProduct(
        product.id,
        product.quantity,
        product.price,
        product.discount
      );
    }
  }

  const value: HarvestContextType = {
    harvestProducts,
    loading,
    error,
    fetchHarvestProducts,
    createHarvestProduct,
    updateHarvestProduct,
    deleteHarvestProduct,
    refreshHarvestProducts,
    updateHarvestInventory,
  };

  return (
    <HarvestContext.Provider value={value}>{children}</HarvestContext.Provider>
  );
};
